import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { PencilSimple, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 1 — Staff Management. Per-staff shift configuration that downstream
// modules (Attendance, Leave, Salary) read for full-day / half-day / late /
// weekly-off calculations.

const WEEKDAYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const DEFAULT_SHIFT = {
  start_time: '09:30',
  end_time: '18:00',
  break_minutes: 30,
  grace_minutes: 15,
  min_hours_full_day: 8.0,
  min_hours_half_day: 4.0,
  weekly_off_days: ['sunday'],
  monthly_paid_leave_limit: 1,
};

export default function ShiftSettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(DEFAULT_SHIFT);

  const fetchUsers = () => {
    api.get('/users')
      .then(r => setUsers(r.data.filter(u => u.active !== false)))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const openEdit = (u) => {
    setEditTarget(u);
    const s = u.shift || {};
    setForm({
      start_time: s.start_time || DEFAULT_SHIFT.start_time,
      end_time: s.end_time || DEFAULT_SHIFT.end_time,
      break_minutes: s.break_minutes ?? DEFAULT_SHIFT.break_minutes,
      grace_minutes: s.grace_minutes ?? DEFAULT_SHIFT.grace_minutes,
      min_hours_full_day: s.min_hours_full_day ?? DEFAULT_SHIFT.min_hours_full_day,
      min_hours_half_day: s.min_hours_half_day ?? DEFAULT_SHIFT.min_hours_half_day,
      weekly_off_days: s.weekly_off_days?.length ? s.weekly_off_days : DEFAULT_SHIFT.weekly_off_days,
      monthly_paid_leave_limit: s.monthly_paid_leave_limit ?? DEFAULT_SHIFT.monthly_paid_leave_limit,
    });
  };

  const toggleDay = (key) => {
    setForm(prev => ({
      ...prev,
      weekly_off_days: prev.weekly_off_days.includes(key)
        ? prev.weekly_off_days.filter(d => d !== key)
        : [...prev.weekly_off_days, key],
    }));
  };

  const save = async () => {
    if (!editTarget) return;
    try {
      await api.put(`/users/${editTarget.id}`, { shift: form });
      toast.success(`Shift updated for ${editTarget.name}`);
      setEditTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save shift');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-[#EF4444]">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="shift-settings-page">
      <div>
        <p className="text-xs text-[#52525B]">
          Per-staff shift timing — Attendance and Salary modules read these values to compute late/half-day/absent automatically.
        </p>
      </div>

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-[#52525B]">Loading…</p>
          ) : users.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#52525B]">No active staff yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Staff</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Designation</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Timing</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center">Grace</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center">Full / Half Hrs</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Weekly Off</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center">Paid Leave / Month</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => {
                  const s = u.shift || DEFAULT_SHIFT;
                  return (
                    <TableRow key={u.id} className="border-[#E4E4E7]" data-testid={`shift-row-${u.id}`}>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2.5">
                          {u.photo ? (
                            <img src={u.photo} alt={u.name} className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 bg-[#3B82F6] rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                              {(u.name || '?').charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-[10px] text-[#A1A1AA] capitalize">{u.role}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-[#52525B]">{u.designation || <span className="text-[#A1A1AA]">—</span>}</TableCell>
                      <TableCell className="text-xs font-mono">{s.start_time} — {s.end_time}</TableCell>
                      <TableCell className="text-xs text-center font-mono">{s.grace_minutes ?? 15}m</TableCell>
                      <TableCell className="text-xs text-center font-mono">
                        {(s.min_hours_full_day ?? 8)}h / {(s.min_hours_half_day ?? 4)}h
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {(s.weekly_off_days || []).map(d => (
                            <Badge key={d} variant="outline" className="rounded-md text-[9px] uppercase bg-[#E0E7FF] text-[#4338CA] border-[#C7D2FE]">
                              {d.slice(0, 3)}
                            </Badge>
                          ))}
                          {(s.weekly_off_days || []).length === 0 && <span className="text-[10px] text-[#A1A1AA]">None</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-center font-mono">{s.monthly_paid_leave_limit ?? 1}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openEdit(u)} data-testid={`edit-shift-${u.id}`}>
                          <PencilSimple size={14} className="mr-1" /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={18} className="text-[#3B82F6]" />
              Shift Settings — {editTarget?.name}
            </DialogTitle>
            <DialogDescription>Sets the working window and attendance thresholds for this staff member.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Start Time</Label>
              <Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="rounded-lg mt-1" data-testid="shift-start" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">End Time</Label>
              <Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="rounded-lg mt-1" data-testid="shift-end" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Break (minutes)</Label>
              <Input type="number" min={0} value={form.break_minutes} onChange={e => setForm({ ...form, break_minutes: parseInt(e.target.value) || 0 })} className="rounded-lg mt-1" data-testid="shift-break" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Grace for Late (minutes)</Label>
              <Input type="number" min={0} value={form.grace_minutes} onChange={e => setForm({ ...form, grace_minutes: parseInt(e.target.value) || 0 })} className="rounded-lg mt-1" data-testid="shift-grace" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Min Hours — Full Day</Label>
              <Input type="number" step="0.5" min={0} value={form.min_hours_full_day} onChange={e => setForm({ ...form, min_hours_full_day: parseFloat(e.target.value) || 0 })} className="rounded-lg mt-1" data-testid="shift-full" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Min Hours — Half Day</Label>
              <Input type="number" step="0.5" min={0} value={form.min_hours_half_day} onChange={e => setForm({ ...form, min_hours_half_day: parseFloat(e.target.value) || 0 })} className="rounded-lg mt-1" data-testid="shift-half" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Weekly Off Days</Label>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {WEEKDAYS.map(d => {
                  const active = form.weekly_off_days.includes(d.key);
                  return (
                    <button
                      type="button"
                      key={d.key}
                      onClick={() => toggleDay(d.key)}
                      data-testid={`shift-weekday-${d.key}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-[#3B82F6] text-white border-[#3B82F6]'
                          : 'bg-white text-[#52525B] border-[#E4E4E7] hover:bg-[#F4F4F5]'
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Monthly Paid Leave Limit</Label>
              <Input type="number" min={0} value={form.monthly_paid_leave_limit} onChange={e => setForm({ ...form, monthly_paid_leave_limit: parseInt(e.target.value) || 0 })} className="rounded-lg mt-1 w-32" data-testid="shift-paid-leave" />
              <p className="text-[10px] text-[#A1A1AA] mt-1">Number of paid leave days allowed each month (over and above weekly offs).</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-shift-btn">
              Save Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
